import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Search, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const Y = '#FFCC00', INK = '#111';
const hasToken = () => !!localStorage.getItem('tekstar_token');
const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function AdminCourier() {
  const [metrics, setMetrics] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [denied, setDenied] = useState(false);
  const [cod, setCod] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [savingPrice, setSavingPrice] = useState(false);

  // Notification log state
  const NOTIF_LIMIT = 50;
  const [notifLogs, setNotifLogs] = useState([]);
  const [notifTotals, setNotifTotals] = useState({ sent: 0, failed: 0, matched: 0 });
  const [notifChannel, setNotifChannel] = useState('');
  const [notifStatus, setNotifStatus] = useState('');
  const [notifHasMore, setNotifHasMore] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const loadShipments = () => {
    const params = {};
    if (q) params.q = q;
    if (statusFilter) params.status = statusFilter;
    api.get('/courier/admin/shipments', { params }).then(({ data }) => setShipments(data.shipments)).catch(() => setDenied(true));
  };

  const loadNotifs = (reset = true) => {
    const skip = reset ? 0 : notifLogs.length;
    const params = { limit: NOTIF_LIMIT, skip };
    if (notifChannel) params.channel = notifChannel;
    if (notifStatus) params.status = notifStatus;
    setNotifLoading(true);
    api.get('/courier/admin/notifications', { params })
      .then(({ data }) => {
        setNotifLogs((prev) => (reset ? data.logs : [...prev, ...data.logs]));
        setNotifTotals(data.totals);
        setNotifHasMore(data.hasMore);
      })
      .catch(() => {})
      .finally(() => setNotifLoading(false));
  };

  const resend = async (log) => {
    setResendingId(log.id);
    try {
      const { data } = await api.post(`/courier/admin/notifications/${log.id}/resend`);
      toast({ title: 'Resent', description: data.message || `Retried ${log.channel} to ${log.recipient}` });
      loadNotifs(true);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not resend notification.', variant: 'destructive' });
    } finally {
      setResendingId(null);
    }
  };

  useEffect(() => {
    if (!hasToken()) { setDenied(true); return; }
    api.get('/courier/admin/metrics').then(({ data }) => setMetrics(data)).catch(() => setDenied(true));
    api.get('/courier/admin/cod').then(({ data }) => setCod(data)).catch(() => {});
    api.get('/courier/admin/pricing').then(({ data }) => setPricing(data.config)).catch(() => {});
    loadShipments();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (denied) return;
    loadNotifs(true);
  }, [notifChannel, notifStatus]); // eslint-disable-line

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      await api.put('/courier/admin/pricing', {
        express: pricing.express, economy: pricing.economy, platformFee: pricing.platformFee,
      });
      toast({ title: 'Saved', description: 'Rate card updated. New quotes use it immediately.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Could not save pricing.', variant: 'destructive' });
    } finally { setSavingPrice(false); }
  };

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center" data-testid="admincourier-denied">
          <Package className="h-10 w-10 mx-auto text-gray-300 mb-3" />
          <p className="font-bold text-xl">Admin access required</p>
          <Link to="/login" className="inline-block mt-4 px-6 py-3 rounded-xl font-black text-black" style={{ background: Y }}>Sign In</Link>
        </div>
      </div>
    );
  }

  const t = metrics?.totals || {};
  const funnel = (metrics?.funnel || []).map((f) => ({ name: humanize(f.status), count: f.count }));

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-courier">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-black flex items-center gap-2"><Package className="h-6 w-6" style={{ color: '#D40511' }} /> Courier Dashboard</h1>
          <Link to="/courier" className="text-sm font-bold text-gray-500 hover:text-black">Open Courier site →</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            ['Shipments', t.shipments ?? 0, 'admincourier-total'],
            ['Revenue', `K${(t.revenue ?? 0).toLocaleString()}`, 'admincourier-revenue'],
            ['Platform Fees', `K${(t.platformFees ?? 0).toLocaleString()}`, 'admincourier-fees'],
            ['Delivered', t.delivered ?? 0, 'admincourier-delivered'],
            ['On-time %', `${t.onTimeRate ?? 0}%`, 'admincourier-ontime'],
          ].map(([l, v, tid]) => (
            <div key={l} className="bg-white rounded-xl p-4 border border-gray-100">
              <p className="text-xs text-gray-500">{l}</p>
              <p className="text-xl font-black" data-testid={tid}>{v}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-100 mb-6">
          <p className="font-bold mb-3">Status funnel</p>
          <div style={{ width: '100%', height: 220 }} data-testid="admincourier-funnel">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill={INK} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* COD + Pricing editor */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl p-5 border border-gray-100" data-testid="admincourier-cod">
            <p className="font-bold mb-3">Cash on Delivery</p>
            <div className="grid grid-cols-3 gap-3">
              <div><p className="text-xs text-gray-500">Collected</p><p className="text-lg font-black text-green-700" data-testid="cod-collected">K{(cod?.totals?.collected ?? 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-gray-500">Outstanding</p><p className="text-lg font-black text-amber-600" data-testid="cod-outstanding">K{(cod?.totals?.outstanding ?? 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-gray-500">COD Shipments</p><p className="text-lg font-black" data-testid="cod-count">{cod?.totals?.codShipments ?? 0}</p></div>
            </div>
            {cod?.shipments?.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto text-sm">
                <div className="flex justify-between py-1 text-[11px] font-bold text-gray-400 uppercase tracking-wide border-b">
                  <span>AWB</span><span>Amount</span><span>Status</span>
                </div>
                {cod.shipments.slice(0, 10).map((s) => (
                  <div key={s.id} className="flex justify-between py-1 border-b last:border-0">
                    <span className="font-mono">{s.awb}</span>
                    <span>K{(s.cod?.amount || 0).toLocaleString()}</span>
                    <span className={s.status === 'delivered' ? 'text-green-700' : 'text-amber-600'}>{humanize(s.status)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {pricing && (
            <div className="bg-white rounded-xl p-5 border border-gray-100" data-testid="admincourier-pricing">
              <p className="font-bold mb-3">Rate Card (new quotes use this instantly)</p>
              {['express', 'economy'].map((svc) => (
                <div key={svc} className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="w-20 text-sm font-bold capitalize">{svc}</span>
                  <label className="text-xs text-gray-500">Base K</label>
                  <input type="number" className="w-20 border rounded px-2 py-1 text-sm" data-testid={`price-${svc}-base`}
                    value={pricing[svc].base} onChange={(e) => setPricing({ ...pricing, [svc]: { ...pricing[svc], base: Number(e.target.value) } })} />
                  <label className="text-xs text-gray-500">per Kg K</label>
                  <input type="number" className="w-20 border rounded px-2 py-1 text-sm" data-testid={`price-${svc}-perkg`}
                    value={pricing[svc].perKg} onChange={(e) => setPricing({ ...pricing, [svc]: { ...pricing[svc], perKg: Number(e.target.value) } })} />
                </div>
              ))}
              <div className="flex items-center gap-3 mb-3">
                <span className="w-20 text-sm font-bold">Platform fee</span>
                <input type="number" className="w-20 border rounded px-2 py-1 text-sm" data-testid="price-platform-fee"
                  value={pricing.platformFee} onChange={(e) => setPricing({ ...pricing, platformFee: Number(e.target.value) })} />
              </div>
              <button onClick={savePricing} disabled={savingPrice} className="px-4 py-2 rounded-lg font-black text-black" style={{ background: Y }} data-testid="price-save-btn">
                {savingPrice ? 'Saving…' : 'Save Rate Card'}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="font-bold">Shipments</p>
            <div className="flex gap-2">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-sm border rounded-lg px-2 py-1.5" data-testid="admincourier-status-filter">
                <option value="">All statuses</option>
                {['created', 'received', 'shipped', 'out_for_delivery', 'delivered', 'exception', 'returned'].map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
              </select>
              <div className="flex">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="AWB / name" className="text-sm border rounded-l-lg px-2 py-1.5" data-testid="admincourier-search-input" onKeyDown={(e) => e.key === 'Enter' && loadShipments()} />
                <button onClick={loadShipments} className="px-3 rounded-r-lg" style={{ background: Y }} data-testid="admincourier-search-btn"><Search className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admincourier-table">
              <thead><tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 pr-3">AWB</th><th className="py-2 pr-3">Route</th><th className="py-2 pr-3">Sender → Receiver</th><th className="py-2 pr-3">Service</th><th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3">Status</th>
              </tr></thead>
              <tbody>
                {shipments.map((s) => (
                  <tr key={s.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono font-bold">{s.awb}</td>
                    <td className="py-2 pr-3">{s.origin?.zoneName} → {s.destination?.zoneName}</td>
                    <td className="py-2 pr-3 text-gray-600">{s.sender?.name} → {s.receiver?.name}</td>
                    <td className="py-2 pr-3">{s.serviceName}</td>
                    <td className="py-2 pr-3 text-right font-mono">K{Number(s.pricing?.total || 0).toFixed(2)}</td>
                    <td className="py-2 pr-3"><span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: Y }}>{humanize(s.status)}</span></td>
                  </tr>
                ))}
                {shipments.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-gray-400">No shipments.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notification delivery log */}
        <div className="bg-white rounded-xl p-5 border border-gray-100 mt-6" data-testid="admincourier-notifications">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="font-bold">Notification Log</p>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs font-bold ${notifTotals.failed > notifTotals.sent ? 'text-red-600' : 'text-gray-500'}`} data-testid="notif-summary">
                {notifTotals.failed > notifTotals.sent ? '⚠ ' : ''}{notifTotals.sent} sent · {notifTotals.failed} failed
              </span>
              <select value={notifChannel} onChange={(e) => setNotifChannel(e.target.value)} className="text-sm border rounded-lg px-2 py-1.5" data-testid="notif-channel-filter">
                <option value="">All channels</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
              <select value={notifStatus} onChange={(e) => setNotifStatus(e.target.value)} className="text-sm border rounded-lg px-2 py-1.5" data-testid="notif-status-filter">
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm" data-testid="notif-table">
              <thead><tr className="text-left text-xs text-gray-500 border-b sticky top-0 bg-white">
                <th className="py-2 pr-3">Time</th><th className="py-2 pr-3">AWB</th><th className="py-2 pr-3">Channel</th><th className="py-2 pr-3">To</th><th className="py-2 pr-3">Stage</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Detail</th><th className="py-2 pr-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {notifLogs.map((l, i) => (
                  <tr key={l.id || i} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
                    <td className="py-2 pr-3 font-mono">{l.awb}</td>
                    <td className="py-2 pr-3 uppercase text-xs font-bold">{l.channel}</td>
                    <td className="py-2 pr-3 text-gray-600">{l.recipient} {l.party ? `(${l.party})` : ''}</td>
                    <td className="py-2 pr-3">{humanize(l.milestone)}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${l.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {l.success ? 'Sent' : 'Failed'}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-400 max-w-[220px] truncate" title={l.error || ''}>{l.error || '—'}</td>
                    <td className="py-2 pr-3 text-right">
                      {!l.success && l.id && (
                        <button
                          onClick={() => resend(l)}
                          disabled={resendingId === l.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                          data-testid={`notif-resend-${l.id}`}
                          title="Re-attempt delivery"
                        >
                          <RefreshCw className={`h-3 w-3 ${resendingId === l.id ? 'animate-spin' : ''}`} />
                          {resendingId === l.id ? 'Sending…' : 'Resend'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {notifLogs.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-gray-400">{notifLoading ? 'Loading…' : 'No notifications match these filters.'}</td></tr>}
              </tbody>
            </table>
          </div>
          {notifHasMore && (
            <div className="text-center mt-3">
              <button onClick={() => loadNotifs(false)} disabled={notifLoading} className="px-4 py-2 rounded-lg text-sm font-bold border border-gray-200 hover:bg-gray-50 disabled:opacity-50" data-testid="notif-load-more">
                {notifLoading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
